import React, { useMemo, useState, useEffect } from 'react';
import { Tag, Tooltip } from 'antd';
import { ApprovalWorkflowItemDto } from '@api/Api';
import moment from 'moment';

type AssetStatusProps = {
  deadline?: string | null;
  status:
    | 'in_progress'
    | 'approved'
    | 'no_decision'
    | 'skipped'
    | 'paused'
    | 'overdue'
    | 'changes_required'
    | 'pending'
    | 'approved_with_feedback';
  approvalWorkflowStatus: ApprovalWorkflowItemDto['status'];
  approvalWorkflowIsPaused?: boolean;
  showOverdueTag?: boolean;
  showStatusDot?: boolean;
  isCurrentStage?: boolean;
};

type StatusesProps = {
  [status: string]: {
    color?: string;
    border?: string;
    tagColor: string;
    label: string;
  };
};

const statuses: StatusesProps = {
  in_progress: { tagColor: '#FAECCC', label: 'In Review' },
  pending: { tagColor: '#DBDBDB', label: 'Pending' },
  approved: { tagColor: '#92D1BA', label: 'Approved' },
  no_decision: { tagColor: '#B0DAF8', label: 'No decision' },
  skipped: {
    border: '1px solid #DBDBDB',
    tagColor: '#f5f6f5',
    label: 'Skipped'
  },
  paused: { tagColor: '#E5E5E5', label: 'Paused' },
  overdue: { tagColor: '#F5C8BE', label: 'Overdue' },
  changes_required: { tagColor: '#F7CAB0', label: 'Changes Required' },
  approved_with_feedback: {
    tagColor: '#BFC5F4',
    label: 'Approved with Feedback'
  }
};

function Status({
  status,
  isCurrentStage,
  approvalWorkflowStatus,
  approvalWorkflowIsPaused,
  deadline,
  showOverdueTag = false,
  showStatusDot = false
}: AssetStatusProps) {
  const deadlineMoment = useMemo(() => {
    if (!deadline) return null;
    return moment(deadline).startOf('minute');
  }, [deadline]);

  const [isPastDueDate, setIsPastDueDate] = useState(
    moment().startOf('minute').isSameOrAfter(deadlineMoment)
  );

  useEffect(() => {
    if (!deadlineMoment) return;
    const intervalId = setInterval(() => {
      setIsPastDueDate(
        moment().startOf('minute').isSameOrAfter(deadlineMoment)
      );
    }, 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, [deadlineMoment]);

  const tag = useMemo(() => {
    if (showOverdueTag && isPastDueDate) return statuses.overdue;
    if (approvalWorkflowIsPaused) return statuses.paused;
    if (approvalWorkflowStatus === 'pending') return statuses.in_progress;
    if (isCurrentStage && status === 'pending') return statuses.in_progress;

    return statuses[status];
  }, [
    isPastDueDate,
    showOverdueTag,
    isCurrentStage,
    status,
    approvalWorkflowIsPaused,
    approvalWorkflowStatus
  ]);

  const tagStyle = useMemo(() => {
    if (!tag) return undefined;
    const style = {} as any;
    if (tag.border) style.border = tag.border;
    if (tag.color) style.color = tag.color;
    return style;
  }, [tag]);

  if (!tag) return null;

  return (
    <Tooltip
      title={tag.label}
      overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
    >
      <span className="status_container">
        <Tag style={tagStyle} color={tag.tagColor}>
          {tag.label}
          {showStatusDot && <span className="status_dot" />}
        </Tag>
      </span>
    </Tooltip>
  );
}

export default Status;
